/**
 * Custom hook — WebSocket connection for job progress.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { connectJobSocket } from "../api/client";

/**
 * Opens a WS to /api/ws/jobs/{jobId} and tracks progress.
 *
 * @param {string|null} jobId  — The job ID to watch (null = inactive).
 * @returns {{ progress, previewImage, status, error, urls }}
 */
export default function useJobSocket(jobId) {
  const [progress, setProgress] = useState(0);
  const [previewImages, setPreviewImages] = useState([]);
  const [status, setStatus] = useState("idle");
  const [statusLog, setStatusLog] = useState("");
  const [error, setError] = useState(null);
  const [urls, setUrls] = useState({
    preview_url: null,
    svg_url: null,
    pdf_url: null,
    xml_url: null,
  });

  const [duration, setDuration] = useState(0);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const wsRef = useRef(null);

  const reset = useCallback(() => {
    setProgress(0);
    setPreviewImages([]);
    setStatus("idle");
    setStatusLog("");
    setError(null);
    setUrls({ preview_url: null, svg_url: null, pdf_url: null, xml_url: null });
    setDuration(0);
    startTimeRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (!jobId) return;

    reset();
    setStatus("connecting");
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    const ws = connectJobSocket(jobId);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("watching");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "status") {
          setStatus(data.status);
          setProgress(data.progress ?? 0);

          if (data.status_log) {
            setStatusLog(data.status_log);
          }

          if (data.preview_images && Array.isArray(data.preview_images) && data.preview_images.length > 0) {
            setPreviewImages(data.preview_images.map(b64 => `data:image/png;base64,${b64}`));
          } else if (data.preview_image) {
            setPreviewImages([`data:image/png;base64,${data.preview_image}`]);
          }

          if (data.status === "completed") {
            setUrls({
              preview_url: data.preview_url || null,
              svg_url: data.svg_url || null,
              pdf_url: data.pdf_url || null,
              xml_url: data.xml_url || null,
            });
            if (timerRef.current) clearInterval(timerRef.current);
          }

          if (data.status === "failed") {
            setError(data.error || "Generation failed");
            if (timerRef.current) clearInterval(timerRef.current);
          }
        }

        if (data.type === "error") {
          setError(data.message || "WebSocket error");
          setStatus("failed");
          if (timerRef.current) clearInterval(timerRef.current);
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    ws.onerror = () => {
      setError("WebSocket connection error");
      setStatus("failed");
      if (timerRef.current) clearInterval(timerRef.current);
    };

    ws.onclose = () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (wsRef.current && wsRef.current.readyState <= 1) {
        wsRef.current.close();
      }
    };
  }, [jobId, reset]);

  return { progress, previewImages, status, error, urls, statusLog, duration, reset };
}
